import ErrorFallback from 'components/error-fallback/ErrorFallback';
import { HelpBox } from 'components/v2/help-boxes';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import { ConfirmDialog } from 'primereact/confirmdialog';
import React, { MutableRefObject, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import EpdRoutes, { EpdLinks } from 'routes/EpdRoutes';
import AuthService from 'services/AuthService';
import EpdService from 'services/EpdService';
import { postCreateDocument } from 'services/api/dynamicFormsApi';
import styled from 'styled-components';
import { ButtonSmall } from 'styles/Styles.styled';
import { CheckBoxPanel, HelpBoldText } from 'styles/v2/Styles.styled';
import MyRadioButton from 'components/controls/MyRadioButton';
import { Modal } from 'util/Modal';
import OverlaySpinner from 'components/spinners/OverlaySpinner';
import { PrimaryButton, SecondaryButton } from 'components/v2/buttons';
import { ProcessStatus } from '../../constants/constants';
import CopyEpd from './CopyEpd';

const CreateEpd: React.FunctionComponent<{
  anyEpd: boolean;
  isDynamicFormsEnabled: boolean;
}> = ({ anyEpd, isDynamicFormsEnabled }) => {
  const history = useHistory();
  const modalRef = useRef<Modal>();
  const [status, setStatus] = React.useState<ProcessStatus>(ProcessStatus.None);
  const { t } = useTranslation();
  const [copying, setCopying] = useState<boolean>(false);
  const [isCreateDialogOpened, setIsCreateDialogOpened] = useState<boolean>(false);
  const { companyId, companyAccountId } = React.useContext(CompanyContext);
  const [epdFormat, setEpdFormat] = useState<'compiler' | 'wizard' | 'dynamic' | null>(null);

  const createEpd = async () => {
    if (!companyAccountId || (epdFormat !== 'compiler' && epdFormat !== 'wizard' && epdFormat !== 'dynamic')) {
      return;
    }
    try {
      setStatus(ProcessStatus.Fetching);
      let route: string;
      switch (epdFormat) {
        case 'compiler':
          route = EpdLinks.epd((await EpdService.createCompilerEpd(companyAccountId)).id);
          break;
        case 'wizard':
          route = EpdLinks.epd((await EpdService.createEpd(companyAccountId)).id);
          break;
        case 'dynamic':
          route = `/dynamic-forms/${await postCreateDocument({ accountId: companyAccountId })}`;
          break;
        default:
          throw new Error('Invalid EPD format');
      }
      history.push(route);
      setStatus(ProcessStatus.Success);
    } catch {
      setStatus(ProcessStatus.Error);
    }
  };

  if (!companyAccountId) {
    return null;
  }

  const onCloseDialog = () => {
    setCopying(false);
  };

  const handleClickNewEpd = (e: any) => {
    if (status === ProcessStatus.Fetching) {
      e.preventDefault();
      return;
    }

    setIsCreateDialogOpened(true);
  };

  const acceptHandler = () => {
    createEpd();
  };

  const handleCancel = () => {
    setEpdFormat(null);
    setIsCreateDialogOpened(false);
  };

  const handleCopyEpdClick = () => {
    history.push(EpdRoutes.copyEpd);
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div>
        {copying && companyId ? (
          <Modal ref={modalRef as MutableRefObject<Modal>}>
            <CopyEpd companyId={companyId} accountId={companyAccountId} onClose={onCloseDialog} />
          </Modal>
        ) : null}
        {!AuthService.isLicenseeAdmin() && (
          <SecondaryButton
            style={{ marginRight: 16, backgroundColor: 'transparent' }}
            label={t('epdDashboard.copyEpd.created')}
            onClick={handleCopyEpdClick}
            disabled={!anyEpd}
          />
        )}
        <PrimaryButton
          label={status === ProcessStatus.Fetching ? t('epdDashboard.newEpd.creating') : t('epdDashboard.newEpd.created')}
          onClick={handleClickNewEpd}
        />
        <ConfirmDialog
          header="Choose your preferred EPD format"
          footer={
            <>
              <SecondaryButton label="Cancel" onClick={handleCancel} />
              <PrimaryButton
                label={status === ProcessStatus.Fetching ? 'Submitting...' : 'Submit'}
                onClick={acceptHandler}
                disabled={!epdFormat || status === ProcessStatus.Fetching}
              />
            </>
          }
          closable={false}
          visible={isCreateDialogOpened}
          style={{ maxWidth: '35rem' }}
          onHide={handleCancel}
          accept={acceptHandler}
          className="custom-dialog"
          message={
            <StyledContentContainer>
              {status === ProcessStatus.Fetching && (
                <div>
                  <OverlaySpinner />
                </div>
              )}
              <HelpBox>
                <span>
                  The EPD Compiler is a feature within the EPD Portal that allows you to create your EPD in a fully
                  digitalized format, seamlessly facilitating data compilation. This format is currently available only for{' '}
                  <HelpBoldText>construction products</HelpBoldText>.
                  <p>
                    If you're working on a product outside of construction or prefer the traditional method, you can still
                    use the <HelpBoldText>Standard EPD format.</HelpBoldText>
                  </p>
                </span>
              </HelpBox>
              <CheckBoxPanel>
                <MyRadioButton
                  inputId="createNewEpd_compiler"
                  value="compiler"
                  name="editMode"
                  checked={epdFormat === 'compiler'}
                  onChange={(value) => setEpdFormat(value)}
                  style={{ marginRight: 6 }}
                />
                <StyledLabel htmlFor={'createNewEpd_compiler'}>
                  Use EPD Compiler format (only for construction products)
                </StyledLabel>
              </CheckBoxPanel>
              <CheckBoxPanel style={{ marginTop: '0.5rem' }}>
                <MyRadioButton
                  inputId="createNewEpd_wizard"
                  value="wizard"
                  name="editMode"
                  checked={epdFormat === 'wizard'}
                  onChange={(value) => setEpdFormat(value)}
                  style={{ marginRight: 6 }}
                />
                <StyledLabel htmlFor={'createNewEpd_wizard'}>Use Standard EPD format</StyledLabel>
              </CheckBoxPanel>
              {isDynamicFormsEnabled && (
                <CheckBoxPanel style={{ marginTop: '0.5rem' }}>
                  <MyRadioButton
                    inputId="createNewEpd_dynamic"
                    value="dynamic"
                    name="editMode"
                    checked={epdFormat === 'dynamic'}
                    onChange={(value) => setEpdFormat(value)}
                    style={{ marginRight: 6 }}
                  />
                  <StyledLabel htmlFor={'createNewEpd_dynamic'}>Dynamic EPD format</StyledLabel>
                </CheckBoxPanel>
              )}
            </StyledContentContainer>
          }
        />
      </div>
    </ErrorBoundary>
  );
};

const StyledLabel = styled.label`
  text-transform: none;
  align-self: center;
  ${(props) => props.theme.typography.bodyInput}
  color: ${(props) => props.theme.colors.primaryBlack};
`;

const StyledContentContainer = styled.div`
  padding: 0 2rem 2rem 2rem;
  ${(props) => props.theme.fonts.textSmall}
`;

export default CreateEpd;
